import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  smsService,
} from "../services/api";
import type {
  SmsGenerateCodeResponse,
} from "../services/api";
import {
  extractErrorMessage,
  extractRetryAfterSeconds,
} from "../utils/errors";
import {
  isValidE164,
} from "../utils/validation";
import {
  OnboardingEvents,
  trackEvent,
} from "../analytics/posthog";
import {
  useAuth,
} from "../hooks/useAuth";
import {
  useCountdown,
} from "../hooks/useCountdown";
import {
  useOtpInput,
} from "../hooks/useOtpInput";
import {
  usePairingSession,
} from "../hooks/usePairingSession";
import {
  ChannelModalShell,
} from "./pairing/ChannelModalShell";
import {
  OtpEntry,
} from "./pairing/OtpEntry";
import {
  PairingCooldownNotice,
} from "./pairing/PairingCooldownNotice";

interface SMSPairingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const CODE_LENGTH = 6;
// Scoped to the user: sessionStorage outlives a logout within the same tab, and
// a shared key would resume the previous user's number on the verify step.
const PAIRING_SESSION_KEY_PREFIX = "catch.pairing.sms:";

const SMS_CONSENT_REQUIRED_MESSAGE =
  "Please check the box to agree to receive texts from Catch.";

export function SMSPairingModal({
  isOpen,
  onClose,
  onSuccess,
}: SMSPairingModalProps) {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [smsConsent, setSmsConsent] = useState(false);
  const { user } = useAuth();
  const pairing = usePairingSession<SmsGenerateCodeResponse>(
    `${PAIRING_SESSION_KEY_PREFIX}${user?.id ?? "anonymous"}`,
  );
  const verificationData = pairing.data;
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  // Resume on the code-entry step when a still-valid code survived the last
  // close or reload, rather than sending the user back to a form the server
  // will not accept for another ten minutes.
  const [step, setStep] = useState<"phone" | "verify">(
    pairing.data ? "verify" : "phone",
  );
  const phoneInputRef = useRef<HTMLInputElement>(null);
  const modalOpenTracked = useRef(false);
  // True when the number in the field came from the pre-signup chat rather
  // than from the user typing it, so the copy can say where it came from.
  const [prefilledFromSignup, setPrefilledFromSignup] = useState(false);

  const verifyRef = useRef<(code: string) => void>(() => {});
  const otp = useOtpInput(CODE_LENGTH, (code) => verifyRef.current(code));
  const countdown = useCountdown(verificationData?.expires_at);
  const cooldown = useCountdown(pairing.cooldownUntil);
  const cooldownActive = cooldown.timeLeft > 0;
  // Never trust the step alone: if the pending code went away underneath it
  // (the session key changed when auth resolved, or a second user signed in),
  // fall back to the form rather than rendering an empty modal.
  const showVerifyStep = step === "verify" && verificationData !== null;

  useEffect(() => {
    if (!isOpen) return;
    if (showVerifyStep) {
      otp.focusFirst();
    } else {
      phoneInputRef.current?.focus();
    }
  }, [isOpen, showVerifyStep, otp.focusFirst]);

  useEffect(() => {
    if (isOpen && !modalOpenTracked.current) {
      trackEvent(OnboardingEvents.SMS_MODAL_OPENED);
      modalOpenTracked.current = true;
    }
    if (!isOpen) {
      modalOpenTracked.current = false;
    }
  }, [isOpen]);

  // Prefill the number the account signed up from, when it texted Marcus
  // before OAuth. The backend records that as an UNCONFIRMED claim and never
  // verifies it itself, because the magic link is a forwardable bearer URL —
  // whoever completed OAuth is not provably whoever held the phone
  // (GET-11931). So this is only ever a suggestion: sending the code to it and
  // typing that code back is what actually binds the number to the account.
  // Never overwrite something the user has already typed.
  // Looked up at most once per open. Re-running as the field changes would
  // fight the user for the input every time they cleared it.
  const prefillAttempted = useRef(false);
  const userTyped = useRef(false);
  useEffect(() => {
    if (!isOpen) {
      setSmsConsent(false);
      prefillAttempted.current = false;
      // Reset alongside it: this only exists to stop a suggestion landing on
      // top of something typed while the request was in flight. Left set, it
      // would suppress the prefill for the rest of the session — including
      // after `resetForm()` empties the field — which reads as the feature
      // silently not working.
      userTyped.current = false;
      return;
    }
    if (prefillAttempted.current || showVerifyStep) return;
    prefillAttempted.current = true;
    let cancelled = false;
    void smsService
      .getStatus()
      .then((status) => {
        // ``userTyped`` so a number entered while the request was in flight
        // isn't overwritten by the suggestion landing behind it.
        if (cancelled || userTyped.current) return;
        if (!status.unconfirmed_phone_number) return;
        setPhoneNumber(status.unconfirmed_phone_number);
        setPrefilledFromSignup(true);
      })
      .catch(() => {
        // A suggestion is a nicety — the form works perfectly well empty, so
        // a failed status read must not surface an error here.
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, showVerifyStep]);

  /** Per-open UI state only; the pending attempt is deliberately untouched. */
  const resetTransientState = () => {
    setError(null);
    // Consent is per number and per visit: a box left ticked would reappear
    // pre-checked on the next phone step, which Twilio 30925 rejects.
    setSmsConsent(false);
    setValidationError(null);
    otp.reset();
    setVerifying(false);
  };

  /** Return to an empty phone-number form. */
  const resetForm = () => {
    resetTransientState();
    countdown.reset();
    setPhoneNumber("");
    setPrefilledFromSignup(false);
    setStep("phone");
  };

  /** Send the user back to the form to pair a different number. Keeps the
   *  cooldown, which is still running server-side, so the phone step can say
   *  when the next code is available instead of failing the request. */
  const startOver = () => {
    pairing.clearCode();
    resetForm();
  };

  const handleClose = () => {
    trackEvent(OnboardingEvents.SMS_MODAL_CLOSED);
    // Users close this modal to go read the code they were just sent, so the
    // pending code and step survive; throwing them away stranded users behind
    // the ten-minute resend cooldown with no way to enter a code they had.
    resetTransientState();
    onClose();
  };

  const handlePhoneChange = (value: string) => {
    userTyped.current = true;
    setPhoneNumber(value);
    setPrefilledFromSignup(false);
    if (validationError && (value.trim() === "" || isValidE164(value))) {
      setValidationError(null);
    }
  };

  const handleSendCode = async () => {
    if (!smsConsent) {
      setError(SMS_CONSENT_REQUIRED_MESSAGE);
      return;
    }
    const trimmed = phoneNumber.trim();
    if (!trimmed) {
      setValidationError("Please enter your phone number");
      return;
    }
    if (!isValidE164(trimmed)) {
      setValidationError("Phone number must be in E.164 format (e.g., +14155551234)");
      return;
    }

    setLoading(true);
    setError(null);
    setValidationError(null);
    try {
      const data = await smsService.generateVerificationCode(trimmed);
      pairing.saveCode(data);
      otp.reset();
      setStep("verify");
      trackEvent(OnboardingEvents.SMS_CODE_GENERATED);
    } catch (err: any) {
      const retryAfter = extractRetryAfterSeconds(err);
      if (retryAfter !== null) {
        // An earlier code is still in play. Show the wait, not a failure.
        pairing.startCooldown(retryAfter);
        trackEvent(OnboardingEvents.SMS_CODE_RATE_LIMITED, {
          retry_after_seconds: retryAfter,
          had_pending_code: verificationData !== null,
        });
        if (verificationData) {
          setStep("verify");
        }
        return;
      }
      const message = extractErrorMessage(err, "Failed to send verification code");
      trackEvent(OnboardingEvents.SMS_CONNECTION_FAILED, { error: message });
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = useCallback(async (code: string) => {
    if (!verificationData) return;

    setVerifying(true);
    setError(null);
    try {
      const result = await smsService.verifyCode(verificationData.phone_number, code);
      if (result.success) {
        trackEvent(OnboardingEvents.SMS_CONNECTED);
        pairing.clearAll();
        cooldown.reset();
        resetForm();
        onClose();
        onSuccess();
      } else {
        const message = result.message || "Verification failed";
        trackEvent(OnboardingEvents.SMS_CONNECTION_FAILED, { error: message });
        setError(message);
      }
    } catch (err: any) {
      const message = extractErrorMessage(err, "Verification failed");
      trackEvent(OnboardingEvents.SMS_CONNECTION_FAILED, { error: message });
      setError(message);
      otp.reset();
      otp.focusFirst();
    } finally {
      setVerifying(false);
    }
  }, [verificationData, onClose, onSuccess, otp.reset, otp.focusFirst]);

  useEffect(() => {
    verifyRef.current = handleVerifyCode;
  }, [handleVerifyCode]);

  return (
    <ChannelModalShell
      isOpen={isOpen}
      dialogOnClose={() => {}}
      onClose={handleClose}
      iconWrapperClass="bg-gray-100"
      icon={
        <svg
          className="h-6 w-6 text-black"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M8.625 12a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H8.25m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H12m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 0 1-2.555-.337A5.972 5.972 0 0 1 5.41 20.97a5.969 5.969 0 0 1-.474-.065 4.48 4.48 0 0 0 .978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25Z"
          />
        </svg>
      }
      title="Connect Your Phone via SMS"
      loading={loading || verifying}
      loadingLabel={loading ? "Sending verification code..." : "Verifying code..."}
      spinnerColorClass="border-blue-600"
      error={error}
    >
      {!showVerifyStep && !loading && (
        <div className="mt-4">
          <div className="mb-4">
            <label
              htmlFor="phone"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Phone Number
            </label>
            <input
              ref={phoneInputRef}
              type="tel"
              id="phone"
              value={phoneNumber}
              onChange={(e) => handlePhoneChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && phoneNumber.trim() && !cooldownActive) {
                  handleSendCode();
                }
              }}
              placeholder="+1234567890"
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                validationError ? "border-red-300" : "border-gray-300"
              }`}
            />
            {validationError ? (
              <p className="mt-1 text-xs text-red-600">{validationError}</p>
            ) : prefilledFromSignup ? (
              <p className="mt-1 text-xs text-gray-500">
                This number reached us before you signed up. Send the code to
                confirm it&apos;s yours — or use a different one.
              </p>
            ) : (
              <p className="mt-1 text-xs text-gray-500">
                Include country code (e.g., +1 for US)
              </p>
            )}
          </div>

          {cooldownActive && (
            <PairingCooldownNotice
              formattedTime={cooldown.formatted}
              codeStillValid={false}
            />
          )}

          {/* SMS consent for A2P 10DLC (GET-10106). Carriers reject consent
              that isn't its own unchecked-by-default control (Twilio 30925),
              and the review checks for each of these phrases verbatim, so
              reword around them rather than through them. */}
          <label className="mt-3 flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={smsConsent}
              onChange={(e) => {
                setSmsConsent(e.target.checked);
                if (e.target.checked && error === SMS_CONSENT_REQUIRED_MESSAGE) {
                  setError(null);
                }
              }}
              className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-primary-500 focus:ring-primary-500 cursor-pointer"
            />
            <span className="text-xs leading-relaxed text-gray-500">
              I agree to receive text messages from Catch, my AI assistant, at
              this number, including my assistant&apos;s replies and updates.
              Message frequency varies. Message and data rates may apply. Reply
              STOP to opt out, HELP for help. See our{" "}
              <a
                href="/terms-new"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-gray-700"
              >
                Terms
              </a>{" "}
              and{" "}
              <a
                href="/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-gray-700"
              >
                Privacy Policy
              </a>
              .
            </span>
          </label>

          <button
            onClick={handleSendCode}
            disabled={!phoneNumber.trim() || !smsConsent || cooldownActive}
            className="w-full mt-3 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Send Verification Code
          </button>
        </div>
      )}

      {showVerifyStep && verificationData && !loading && !verifying && (
        <OtpEntry
          digits={otp.digits}
          refs={otp.refs}
          onChange={otp.handleChange}
          onKeyDown={otp.handleKeyDown}
          length={CODE_LENGTH}
          timeLeft={countdown.timeLeft}
          formattedTime={countdown.formatted}
          infoMessage={
            <>
              We sent a 6-character code to <strong>{verificationData.phone_number}</strong>.
              Enter it below to verify your phone number.
            </>
          }
          notice={
            cooldownActive ? (
              <PairingCooldownNotice
                formattedTime={cooldown.formatted}
                codeStillValid={countdown.timeLeft > 0}
              />
            ) : null
          }
          onRegenerate={() => {
            trackEvent(OnboardingEvents.SMS_CODE_REGENERATE_CLICKED);
            startOver();
          }}
        />
      )}
    </ChannelModalShell>
  );
}
